import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { pprwAPI, trainersAPI, purchaseOrdersAPI, taxonomyAPI, preferencesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell,
  PieChart, Pie, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  LineChart, Line,
} from 'recharts';
import { Plus, Search, Download, Upload, FileSpreadsheet, Copy,
  ChevronLeft, ChevronRight, ChevronDown, MonitorPlay, LayoutGrid, Calendar,
  Users, BarChart2, Filter, X, Loader2, Trash2, FileDown, Eye, Wallet, Layers, Pencil
} from 'lucide-react';
import { TrainerAutocomplete } from '../components/TrainerAutocomplete';
import { TaxonomyCustomFields } from '../components/shared/TaxonomyCustomFields';
import { ColumnsPicker } from '../components/shared/ColumnsPicker';
import { TablePagination, PAGE_SIZE } from '../components/shared/TablePagination';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { ContentItemLinkDialog } from '../components/content-items/ContentItemLinkDialog';
import { Checkbox } from '../components/ui/checkbox';
import { BulkEditDialog } from '../components/pprw/BulkEditDialog';
import { WorkshopsTab } from '../components/pprw/WorkshopsTab';

// ── Constants ─────────────────────────────────────────────────────────────────
const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'];
const TIMEZONES = ['IST', 'BST', 'GMT', 'PST', 'PDT'];
const DELIVERY_TYPES = ['Live', 'Simu Live'];
const DELIVERY_STATUSES = ['Scheduled', 'Upcoming', 'In Progress', 'Completed', 'On Hold', 'Cancelled'];
const SOLUTION_AREAS = ['AI Business Solutions', 'Cloud and AI Platforms', 'Security'];
const LABS_TYPES = ['Interactive Simulated Lab', 'Skillable', 'Spektra', 'Hybrid (Skillable + Simulated)', 'Hybrid (Spektra + Simulated)'];

const TZ_COLORS = { IST: '#3b82f6', BST: '#10b981', GMT: '#f59e0b', PST: '#a855f7', PDT: '#ec4899' };
const STATUS_COLORS = {
  Scheduled: '#3b82f6', Upcoming: '#a855f7', 'In Progress': '#f59e0b',
  Completed: '#10b981', 'On Hold': '#f97316', Cancelled: '#ef4444',
};
const CHART_COLORS = ['#84cc16','#3b82f6','#f59e0b','#10b981','#a855f7','#f97316','#ef4444','#06b6d4'];

const statusBadge = (status) => {
  const cls = {
    Scheduled: 'bg-blue-100 text-blue-700',
    Upcoming: 'bg-purple-100 text-purple-700',
    'In Progress': 'bg-amber-100 text-amber-700',
    Completed: 'bg-emerald-100 text-emerald-700',
    'On Hold': 'bg-orange-100 text-orange-700',
    Cancelled: 'bg-red-100 text-red-700',
  }[status] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status || '—'}</span>;
};

const tzBadge = (tz) => {
  const cls = { IST: 'bg-blue-100 text-blue-700', BST: 'bg-emerald-100 text-emerald-700', GMT: 'bg-amber-100 text-amber-700', PST: 'bg-purple-100 text-purple-700', PDT: 'bg-pink-100 text-pink-700' }[tz] || 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{tz || '—'}</span>;
};

const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const fmtCurrency = (n) => n != null ? `$${Number(n).toLocaleString()}` : '—';

// ── Form Field Wrapper ────────────────────────────────────────────────────────
const FormField = ({ label, children, span2 = false }) => (
  <div className={span2 ? 'col-span-2' : ''}>
    <label className="block text-xs font-medium text-muted-foreground mb-1">{label}</label>
    {children}
  </div>
);

// ── Create / Edit Dialog ──────────────────────────────────────────────────────
function CreateEditDialog({ open, onClose, onSaved, existing, trainers = [], categories = [] }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    course_title: existing?.course_title || '',
    series_journey_title: existing?.series_journey_title || '',
    quarter: existing?.quarter || '',
    solution_area: existing?.solution_area || '',
    delivery_type: existing?.delivery_type || 'Live',
    start_date: existing?.start_date || '',
    end_date: existing?.end_date || '',
    start_time: existing?.start_time || existing?.session_time || '',
    end_time: existing?.end_time || '',
    time_zone: existing?.time_zone || '',
    trainer_1: existing?.trainer_1 || '',
    trainer_2: existing?.trainer_2 || '',
    moderators: existing?.moderators || '',
    content_owner: existing?.content_owner || '',
    lab_owner: existing?.lab_owner || '',
    po_number: existing?.po_number || '',
    training_amount: existing?.training_amount != null ? String(existing.training_amount) : '',
    delivery_status: existing?.delivery_status || 'Scheduled',
    custom_fields: existing?.custom_fields || {},
    content_item_id: existing?.content_item_id || '',
    content_item_title: existing?.content_item_title || '',
    workshop_id: existing?.workshop_id || '',
  });
  const [customFieldKeys, setCustomFieldKeys] = useState(Object.keys(existing?.custom_fields || {}));
  const [contentItemDialogOpen, setContentItemDialogOpen] = useState(false);
  const [workshops, setWorkshops] = useState([]);
  useEffect(() => { pprwAPI.workshops.getAll().then(setWorkshops).catch(() => {}); }, []);
  const selectWorkshop = (val) => {
    if (val === '__none__') { set('workshop_id', ''); return; }
    const w = workshops.find(x => x.id === val);
    setForm(f => ({
      ...f,
      workshop_id: val,
      po_number: w?.po_number || f.po_number,
      solution_area: f.solution_area || w?.solution_area || '',
      delivery_type: w?.delivery_type || f.delivery_type,
      quarter: f.quarter || w?.quarter || '',
    }));
  };
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.course_title.trim()) { toast.error('Course Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        training_amount: form.training_amount ? parseFloat(form.training_amount) : null,
      };
      if (isEdit) {
        await pprwAPI.update(existing.id, payload);
        toast.success('Session updated');
      } else {
        await pprwAPI.create(payload);
        toast.success('Session created');
      }
      onSaved();
      onClose();
    } catch (_e) { toast.error('Failed to save delivery'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pprw-create-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Session' : 'New PPRW Session'}</DialogTitle>
          <DialogDescription>Fill in the details. Additional fields can be completed on the session detail page.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <FormField label="Course Title *" span2>
            <Input value={form.course_title} onChange={e => set('course_title', e.target.value)} placeholder="e.g. Agentic AI Fundamentals for Partners" data-testid="pprw-course-title-input" />
          </FormField>
          <FormField label="Series / Journey Title (optional)" span2>
            <Input value={form.series_journey_title} onChange={e => set('series_journey_title', e.target.value)} placeholder="e.g. GEMINI FY26 Depth vILTs" data-testid="pprw-series-input" />
          </FormField>
          <FormField label="Workshop (optional)" span2>
            <Select value={form.workshop_id || '__none__'} onValueChange={selectWorkshop}>
              <SelectTrigger data-testid="pprw-workshop-select"><SelectValue placeholder="Assign to a workshop…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No workshop (standalone) —</SelectItem>
                {workshops.map(w => <SelectItem key={w.id} value={w.id}>{w.title}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.workshop_id && <p className="text-xs text-muted-foreground mt-1">Common attributes (incl. PO number) are inherited from the workshop.</p>}
          </FormField>
          <FormField label="Linked Content Dev Course" span2>
            {form.content_item_id ? (
              <div className="flex items-center justify-between gap-2 border rounded-lg p-2.5 bg-blue-50 border-blue-200" data-testid="pprw-selected-content-item">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{form.content_item_title}</p>
                  <p className="text-xs text-muted-foreground">Content Dev tracker will flag urgency if this course isn't build-complete yet</p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <Button type="button" variant="outline" size="sm" onClick={() => setContentItemDialogOpen(true)} data-testid="pprw-change-content-item-btn">Change</Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm(f => ({ ...f, content_item_id: '', content_item_title: '' }))} data-testid="pprw-clear-content-item-btn">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="button" variant="outline" className="w-full justify-start" onClick={() => setContentItemDialogOpen(true)} data-testid="pprw-open-content-item-search-btn">
                <Layers className="w-4 h-4 mr-2" />Link to a Content Dev course (optional)...
              </Button>
            )}
          </FormField>
          <FormField label="Solution Area">
            <Select value={form.solution_area} onValueChange={v => set('solution_area', v)}>
              <SelectTrigger data-testid="pprw-solution-area-select"><SelectValue placeholder="Select solution area" /></SelectTrigger>
              <SelectContent>{SOLUTION_AREAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Quarter">
            <Select value={form.quarter} onValueChange={v => set('quarter', v)}>
              <SelectTrigger data-testid="pprw-quarter-select"><SelectValue placeholder="Select quarter" /></SelectTrigger>
              <SelectContent>{QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Start Date">
            <Input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} data-testid="pprw-start-date-input" />
          </FormField>
          <FormField label="End Date">
            <Input type="date" value={form.end_date} onChange={e => set('end_date', e.target.value)} data-testid="pprw-end-date-input" />
          </FormField>
          <FormField label="Delivery Type">
            <Select value={form.delivery_type} onValueChange={v => set('delivery_type', v)}>
              <SelectTrigger data-testid="pprw-delivery-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DELIVERY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Time Zone">
            <Select value={form.time_zone} onValueChange={v => set('time_zone', v)}>
              <SelectTrigger data-testid="pprw-timezone-select"><SelectValue placeholder="Select time zone" /></SelectTrigger>
              <SelectContent>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Trainer 1">
            <TrainerAutocomplete value={form.trainer_1} trainers={trainers} onChange={v => set('trainer_1', v)} onCommit={v => set('trainer_1', v)} placeholder="Search trainer roster…" testId="pprw-trainer1-input" />
          </FormField>
          <FormField label="Trainer 2">
            <TrainerAutocomplete value={form.trainer_2} trainers={trainers} onChange={v => set('trainer_2', v)} onCommit={v => set('trainer_2', v)} placeholder="Optional second trainer" testId="pprw-trainer2-input" />
          </FormField>
          <FormField label="PO Number">
            <Input value={form.po_number} onChange={e => set('po_number', e.target.value)} placeholder="PO-2026-001" data-testid="pprw-po-input" />
          </FormField>
          <FormField label="Training Amount ($)">
            <Input type="number" value={form.training_amount} onChange={e => set('training_amount', e.target.value)} placeholder="5000" data-testid="pprw-amount-input" />
          </FormField>
          <FormField label="Delivery Status">
            <Select value={form.delivery_status} onValueChange={v => set('delivery_status', v)}>
              <SelectTrigger data-testid="pprw-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DELIVERY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </FormField>
          <FormField label="Start Time">
            <Input type="time" value={form.start_time} onChange={e => set('start_time', e.target.value)} data-testid="pprw-start-time-input" />
          </FormField>
          <FormField label="End Time">
            <Input type="time" value={form.end_time} onChange={e => set('end_time', e.target.value)} data-testid="pprw-end-time-input" />
          </FormField>
        </div>
        {categories.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border space-y-4">
            <p className="text-xs font-medium text-muted-foreground">Additional Fields</p>
            <TaxonomyCustomFields
              categories={categories}
              customFieldKeys={customFieldKeys}
              setCustomFieldKeys={setCustomFieldKeys}
              values={form.custom_fields || {}}
              onChange={(v) => set('custom_fields', v)}
              testPrefix="pprw-custom-field"
            />
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="pprw-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : (isEdit ? 'Save Changes' : 'Create Delivery')}
          </Button>
        </div>
      </DialogContent>
      <ContentItemLinkDialog
        open={contentItemDialogOpen}
        onOpenChange={setContentItemDialogOpen}
        onSelect={(item) => { setContentItemDialogOpen(false); setForm(f => ({ ...f, content_item_id: item?.id || '', content_item_title: item?.title || '' })); }}
      />
    </Dialog>
  );
}

// ── Bulk Import Dialog ────────────────────────────────────────────────────────
function BulkImportDialog({ open, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleImport = async () => {
    if (!file) { toast.error('Please select a file'); return; }
    setLoading(true);
    try {
      const res = await pprwAPI.bulkImport(file);
      setResult(res);
      if (res.created > 0) { toast.success(res.message); onImported(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setLoading(false); }
  };

  const reset = () => { setFile(null); setResult(null); onClose(); };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="max-w-lg" data-testid="pprw-bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk Import PPRW Deliveries</DialogTitle>
          <DialogDescription>Upload an XLSX file using the provided template.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="flex items-center gap-2">
            <a href={pprwAPI.getTemplateUrl()} download className="flex-1">
              <Button variant="outline" className="w-full gap-2" data-testid="pprw-download-template-btn">
                <FileSpreadsheet className="w-4 h-4" /> Download Template
              </Button>
            </a>
          </div>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            <input type="file" accept=".xlsx" id="pprw-import-file" className="hidden"
              onChange={e => setFile(e.target.files?.[0] || null)} data-testid="pprw-file-input" />
            <label htmlFor="pprw-import-file" className="cursor-pointer">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">{file ? file.name : 'Click to select .xlsx file'}</p>
            </label>
          </div>
          {result && (
            <div className={`p-3 rounded-lg text-sm ${result.errors?.length ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>
              <p className="font-medium">{result.message}</p>
              {result.errors?.map((e, i) => <p key={i} className="mt-1 text-xs">Row {e.row}: {e.error}</p>)}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={reset}>Cancel</Button>
            <Button onClick={handleImport} disabled={!file || loading} data-testid="pprw-import-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing…</> : 'Import'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Training Tracker Tab ──────────────────────────────────────────────────────
function TrainingTrackerTab({ deliveries, onRefresh, onNavigate, trainers, categories = [] }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({ quarter: new Set(), solution_area: new Set(), time_zone: new Set(), delivery_type: new Set(), delivery_status: new Set(), workshop: new Set() });
  const setColFilter = (k, s) => setColFilters(prev => ({ ...prev, [k]: s }));
  const [showCreate, setShowCreate] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [visibleCustomCols, setVisibleCustomCols] = useState([]);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [creatingWorkshop, setCreatingWorkshop] = useState(false);
  const customColumns = categories.map(c => ({ key: `custom:${c.id}`, taxonomyKey: c.id, label: c.label }));
  const [workshopMap, setWorkshopMap] = useState({});
  useEffect(() => { pprwAPI.workshops.getAll().then(ws => setWorkshopMap(Object.fromEntries((ws || []).map(w => [w.id, w.title])))).catch(() => {}); }, []);
  const allColumns = [{ key: 'special:workshop', taxonomyKey: '__workshop__', label: 'Workshop' }, ...customColumns];

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelectedIds(new Set());

  const handleCreateWorkshopFromSelection = async () => {
    if (selectedIds.size === 0) return;
    setCreatingWorkshop(true);
    try {
      const w = await pprwAPI.workshops.createFromSessions(Array.from(selectedIds));
      toast.success('Workshop created from selection — sessions inherited its common attributes');
      navigate(`/pprw/workshops/${w.id}`);
    } catch { toast.error('Failed to create workshop'); }
    finally { setCreatingWorkshop(false); }
  };

  const afterBulk = () => { onRefresh(); clearSelection(); };

  useEffect(() => {
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.pprw_columns)) setVisibleCustomCols(prefs.pprw_columns);
    }).catch(() => {});
  }, []);
  const toggleCustomCol = (key) => {
    setVisibleCustomCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('pprw_columns', next).catch(() => {});
      return next;
    });
  };

  const filtered = deliveries.filter(d => {
    if (colFilters.quarter.size && !colFilters.quarter.has(d.quarter)) return false;
    if (colFilters.solution_area.size && !colFilters.solution_area.has(d.solution_area)) return false;
    if (colFilters.time_zone.size && !colFilters.time_zone.has(d.time_zone)) return false;
    if (colFilters.delivery_type.size && !colFilters.delivery_type.has(d.delivery_type)) return false;
    if (colFilters.delivery_status.size && !colFilters.delivery_status.has(d.delivery_status)) return false;
    if (colFilters.workshop.size && !colFilters.workshop.has(d.workshop_id || '__none__')) return false;
    if (search) {
      const s = search.toLowerCase();
      return (d.course_title || '').toLowerCase().includes(s) ||
        (d.trainer_1 || '').toLowerCase().includes(s) ||
        (d.po_number || '').toLowerCase().includes(s) ||
        (d.series_journey_title || '').toLowerCase().includes(s) ||
        (d.solution_area || '').toLowerCase().includes(s);
    }
    return true;
  });

  useEffect(() => { setPage(1); }, [search, colFilters]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const allPagedSelected = paged.length > 0 && paged.every(d => selectedIds.has(d.id));
  const toggleSelectAll = () => setSelectedIds(prev => {
    const n = new Set(prev);
    if (allPagedSelected) paged.forEach(d => n.delete(d.id)); else paged.forEach(d => n.add(d.id));
    return n;
  });

  const qOpts = [...new Set(deliveries.map(d => d.quarter).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const saOpts = [...new Set(deliveries.map(d => d.solution_area).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const tzOpts = [...new Set(deliveries.map(d => d.time_zone).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const typeOpts = [...new Set(deliveries.map(d => d.delivery_type).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const statusOpts = [...new Set(deliveries.map(d => d.delivery_status).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
  const hasStandalone = deliveries.some(d => !d.workshop_id);
  const workshopOpts = [
    ...(hasStandalone ? [{ value: '__none__', label: '— No workshop (standalone) —' }] : []),
    ...[...new Set(deliveries.map(d => d.workshop_id).filter(Boolean))]
      .map(id => ({ value: id, label: workshopMap[id] || 'Workshop' }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  // Auto-reveal the Workshop column whenever a workshop filter is active
  const effectiveVisibleCols = colFilters.workshop.size
    ? [...new Set([...visibleCustomCols, 'special:workshop'])]
    : visibleCustomCols;
  const toolbarWorkshopValue = colFilters.workshop.size === 1 ? [...colFilters.workshop][0] : '__all__';

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session?')) return;
    setDeleting(id);
    try { await pprwAPI.delete(id); toast.success('Deleted'); onRefresh(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleCopy = async (id) => {
    try { await pprwAPI.copy(id); toast.success('Session copied'); onRefresh(); }
    catch { toast.error('Copy failed'); }
  };

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center mb-4">
        <div className="relative flex-1 min-w-52">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search course, trainer, PO, series…"
            className="pl-9" data-testid="pprw-search-input" />
        </div>
        {customColumns.length >= 0 && (
          <ColumnsPicker columns={allColumns} visibleKeys={visibleCustomCols} onToggle={toggleCustomCol} testId="pprw-columns" />
        )}
        <Select value={toolbarWorkshopValue} onValueChange={(v) => setColFilter('workshop', v === '__all__' ? new Set() : new Set([v]))}>
          <SelectTrigger className="w-52 h-9" data-testid="pprw-workshop-filter-toolbar"><SelectValue placeholder="Workshop: All" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Workshop: All</SelectItem>
            {workshopOpts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <a href={pprwAPI.getExportUrl({})} download data-testid="pprw-export-btn">
            <Button variant="outline" size="sm" className="gap-2"><FileDown className="w-4 h-4" />Export</Button>
          </a>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowImport(true)} data-testid="pprw-import-open-btn">
            <Upload className="w-4 h-4" />Import
          </Button>
          <Button size="sm" className="gap-2" onClick={() => { setEditTarget(null); setShowCreate(true); }} data-testid="pprw-create-btn">
            <Plus className="w-4 h-4" />New Session
          </Button>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 p-3 rounded-xl border border-lime/40 bg-lime/5" data-testid="pprw-bulk-bar">
          <span className="text-sm font-medium">{selectedIds.size} session{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" variant="outline" className="gap-2" onClick={handleCreateWorkshopFromSelection} disabled={creatingWorkshop} data-testid="pprw-create-workshop-from-selection-btn">
              {creatingWorkshop ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}Create Workshop from selection
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowBulkEdit(true)} data-testid="pprw-bulk-edit-btn">
              <Pencil className="w-4 h-4" />Bulk Edit
            </Button>
            <Button size="sm" variant="ghost" className="gap-1" onClick={clearSelection} data-testid="pprw-clear-selection-btn">
              <X className="w-3.5 h-3.5" />Clear
            </Button>
          </div>
        </div>
      )}

      <ActiveFiltersBar
        filters={[
          { key: 'quarter', label: 'Quarter', set: colFilters.quarter, options: qOpts, onChange: (s) => setColFilter('quarter', s) },
          { key: 'solution_area', label: 'Solution Area', set: colFilters.solution_area, options: saOpts, onChange: (s) => setColFilter('solution_area', s) },
          { key: 'time_zone', label: 'TZ', set: colFilters.time_zone, options: tzOpts, onChange: (s) => setColFilter('time_zone', s) },
          { key: 'delivery_type', label: 'Type', set: colFilters.delivery_type, options: typeOpts, onChange: (s) => setColFilter('delivery_type', s) },
          { key: 'delivery_status', label: 'Status', set: colFilters.delivery_status, options: statusOpts, onChange: (s) => setColFilter('delivery_status', s) },
          { key: 'workshop', label: 'Workshop', set: colFilters.workshop, options: workshopOpts, onChange: (s) => setColFilter('workshop', s) },
        ]}
        search={search}
        onClearSearch={() => setSearch('')}
        testId="pprw-active-filters"
      />

      {/* Table */}
      <div className="rounded-xl border border-border overflow-auto max-h-[calc(100vh-320px)]" data-testid="pprw-table">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0 z-20">
            <tr>
              <th className="px-3 py-3 w-10">
                <Checkbox checked={allPagedSelected} onCheckedChange={toggleSelectAll} data-testid="pprw-select-all" />
              </th>
              {(() => {
                const thc = "px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap";
                const cols = [
                  { label: 'Course Title' },
                  { label: 'Series / Journey' },
                  { label: 'Quarter', k: 'quarter', opts: qOpts },
                  { label: 'Solution Area', k: 'solution_area', opts: saOpts },
                  { label: 'Date' },
                  { label: 'TZ', k: 'time_zone', opts: tzOpts },
                  { label: 'Type', k: 'delivery_type', opts: typeOpts },
                  { label: 'Trainer' },
                  { label: 'Status', k: 'delivery_status', opts: statusOpts },
                ];
                return cols.map(c => (
                  <th key={c.label} className={thc}>
                    {c.k
                      ? <ColumnFilterPopover label={c.label} options={c.opts} selected={colFilters[c.k]} onChange={(s) => setColFilter(c.k, s)} testId={`pprw-col-filter-${c.k}`} />
                      : c.label}
                  </th>
                ));
              })()}
              {allColumns.filter(c => effectiveVisibleCols.includes(c.key)).map(c => (
                <th key={c.key} className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                  {c.key === 'special:workshop'
                    ? <ColumnFilterPopover label={c.label} options={workshopOpts} selected={colFilters.workshop} onChange={(s) => setColFilter('workshop', s)} testId="pprw-col-filter-workshop" />
                    : c.label}
                </th>
              ))}
              <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={11 + effectiveVisibleCols.length} className="px-4 py-12 text-center text-muted-foreground text-sm">
                No sessions found.{' '}
                <button className="text-primary underline" onClick={() => setShowCreate(true)}>Create one</button>
              </td></tr>
            )}
            {paged.map((d) => (
              <tr key={d.id} className="border-t border-border hover:bg-muted/30 transition-colors cursor-pointer group"
                onClick={() => onNavigate(d.id)} data-testid={`pprw-row-${d.id}`}>
                <td className="px-3 py-3" onClick={e => e.stopPropagation()}>
                  <Checkbox checked={selectedIds.has(d.id)} onCheckedChange={() => toggleSelect(d.id)} data-testid={`pprw-select-${d.id}`} />
                </td>
                <td className="px-3 py-3 font-medium max-w-[200px] truncate">
                  <Link to={`/pprw/${d.id}`} onClick={e => e.stopPropagation()}
                    className="hover:underline hover:text-lime-700 transition-colors" data-testid={`pprw-course-link-${d.id}`}>
                    {d.course_title}
                  </Link>
                </td>
                <td className="px-3 py-3 text-muted-foreground text-xs max-w-[140px] truncate">{d.series_journey_title || '—'}</td>
                <td className="px-3 py-3">{d.quarter || '—'}</td>
                <td className="px-3 py-3 text-xs max-w-[120px] truncate">{d.solution_area || '—'}</td>
                <td className="px-3 py-3 text-xs whitespace-nowrap">{fmtDate(d.start_date)}</td>
                <td className="px-3 py-3">{tzBadge(d.time_zone)}</td>
                <td className="px-3 py-3 text-xs">{d.delivery_type || '—'}</td>
                <td className="px-3 py-3 text-xs max-w-[100px] truncate">{d.trainer_1 || '—'}</td>
                <td className="px-3 py-3">{statusBadge(d.delivery_status)}</td>
                {allColumns.filter(c => effectiveVisibleCols.includes(c.key)).map(c => (
                  <td key={c.key} className="px-3 py-3 text-xs max-w-[160px] truncate">
                    {c.taxonomyKey === '__workshop__'
                      ? (d.workshop_id ? (workshopMap[d.workshop_id] || 'Workshop') : '—')
                      : (d.custom_fields?.[c.taxonomyKey] || '—')}
                  </td>
                ))}
                <td className="px-3 py-3">
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                    <button className="p-1.5 rounded hover:bg-muted" title="Edit" onClick={() => { setEditTarget(d); setShowCreate(true); }} data-testid={`pprw-edit-btn-${d.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button className="p-1.5 rounded hover:bg-muted" title="Copy" onClick={() => handleCopy(d.id)} data-testid={`pprw-copy-btn-${d.id}`}>
                      <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                    </button>
                    <button className="p-1.5 rounded hover:bg-red-50" title="Delete" onClick={() => handleDelete(d.id)} disabled={deleting === d.id} data-testid={`pprw-delete-btn-${d.id}`}>
                      {deleting === d.id ? <Loader2 className="w-3.5 h-3.5 animate-spin text-red-400" /> : <Trash2 className="w-3.5 h-3.5 text-red-400" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-2.5 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          Showing {filtered.length} of {deliveries.length} sessions
        </div>
      </div>
      <TablePagination page={page} total={filtered.length} onPageChange={setPage} testId="pprw-pagination" itemLabel="sessions" />

      <CreateEditDialog key={`${editTarget?.id || 'new'}-${showCreate}`} open={showCreate} onClose={() => { setShowCreate(false); setEditTarget(null); }}
        onSaved={onRefresh} existing={editTarget} trainers={trainers} categories={categories} />
      <BulkImportDialog open={showImport} onClose={() => setShowImport(false)} onImported={onRefresh} />
      {showBulkEdit && (
        <BulkEditDialog open={showBulkEdit} onClose={() => setShowBulkEdit(false)} sessionIds={Array.from(selectedIds)} onSaved={afterBulk} />
      )}
    </div>
  );
}

// ── Calendar View Tab ─────────────────────────────────────────────────────────
function CalendarTab({ deliveries, onNavigate }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const monthName = new Date(year, month, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = new Date(year, month, 1).getDay();

  const sessionsByDay = {};
  deliveries.forEach(d => {
    if (!d.start_date) return;
    try {
      const dt = new Date(d.start_date);
      if (dt.getFullYear() === year && dt.getMonth() === month) {
        const day = dt.getDate();
        if (!sessionsByDay[day]) sessionsByDay[day] = [];
        sessionsByDay[day].push(d);
      }
    } catch (_e) { /* skip invalid dates */ }
  });

  const prev = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const next = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isToday = (d) => d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">{monthName}</h3>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={prev} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="icon" onClick={next} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>
      {/* Legend */}
      <div className="flex gap-4 mb-4 text-xs">
        {Object.entries(TZ_COLORS).map(([tz, color]) => (
          <div key={tz} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ background: color }} />
            <span className="text-muted-foreground">{tz}</span>
          </div>
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-7 gap-px bg-border rounded-xl overflow-hidden border border-border">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
          <div key={d} className="bg-muted/50 py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wide">{d}</div>
        ))}
        {cells.map((d, i) => (
          <div key={i} className={`bg-background min-h-[90px] p-1.5 ${d ? 'hover:bg-muted/20' : 'bg-muted/10'}`}>
            {d && (
              <>
                <div className={`text-xs font-medium mb-1 w-6 h-6 flex items-center justify-center rounded-full
                  ${isToday(d) ? 'bg-lime text-secondary' : 'text-foreground'}`}>{d}</div>
                <div className="space-y-0.5">
                  {(sessionsByDay[d] || []).slice(0, 3).map(s => (
                    <button key={s.id} onClick={() => onNavigate(s.id)}
                      className="w-full text-left text-[10px] px-1.5 py-0.5 rounded font-medium text-white truncate"
                      style={{ background: TZ_COLORS[s.time_zone] || '#6b7280' }}
                      title={s.course_title} data-testid={`cal-session-${s.id}`}>
                      {s.course_title}
                    </button>
                  ))}
                  {(sessionsByDay[d] || []).length > 3 && (
                    <div className="text-[10px] text-muted-foreground px-1">+{sessionsByDay[d].length - 3} more</div>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Trainer Management Tab ────────────────────────────────────────────────────
function AssignTrainerDialog({ open, onClose, sessionIds, trainers, onDone, allDeliveries = [] }) {
  const [slot, setSlot] = useState('trainer_1');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  // Trainer load guardrail: flag existing sessions of the chosen trainer that
  // clash (same date + time zone) with any session we're about to assign.
  const conflicts = React.useMemo(() => {
    if (!name.trim()) return [];
    const selected = allDeliveries.filter(d => sessionIds.includes(d.id));
    const targetKeys = new Set(selected.map(d => `${d.start_date}|${d.time_zone}`));
    const selectedIdSet = new Set(sessionIds);
    return allDeliveries.filter(d =>
      !selectedIdSet.has(d.id) &&
      (d.trainer_1 === name || d.trainer_2 === name) &&
      d.start_date && targetKeys.has(`${d.start_date}|${d.time_zone}`)
    );
  }, [name, sessionIds, allDeliveries]);

  const apply = async () => {
    if (!name.trim()) { toast.error('Pick a trainer'); return; }
    setSaving(true);
    try {
      const res = await pprwAPI.bulkUpdate(sessionIds, { [slot]: name });
      toast.success(`Assigned ${name} to ${res.updated} session${res.updated !== 1 ? 's' : ''}`);
      onDone(); onClose();
    } catch (_e) { toast.error('Assign failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="assign-trainer-dialog">
        <DialogHeader>
          <DialogTitle>Assign / Swap Trainer</DialogTitle>
          <DialogDescription>Set the trainer on {sessionIds.length} selected session{sessionIds.length !== 1 ? 's' : ''}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Trainer slot</label>
            <Select value={slot} onValueChange={setSlot}>
              <SelectTrigger data-testid="assign-slot-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="trainer_1">Trainer 1</SelectItem>
                <SelectItem value="trainer_2">Trainer 2</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Trainer</label>
            <TrainerAutocomplete value={name} trainers={trainers} onCommit={setName} placeholder="Search trainer roster…" testId="assign-trainer-input" />
          </div>
          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="trainer-conflict-warning">
              <p className="text-xs font-semibold text-amber-800 mb-1">⚠ {name} already has {conflicts.length} overlapping session{conflicts.length !== 1 ? 's' : ''} (same date &amp; time zone):</p>
              <ul className="text-[11px] text-amber-700 space-y-0.5 max-h-24 overflow-y-auto">
                {conflicts.slice(0, 6).map(c => (
                  <li key={c.id} className="truncate">• {c.course_title} — {c.start_date} · {c.time_zone || 'no TZ'}</li>
                ))}
              </ul>
              <p className="text-[11px] text-amber-700 mt-1">You can still proceed if this is intentional.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={saving} data-testid="assign-trainer-confirm-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Assigning…</> : `Assign to ${sessionIds.length}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TrainerManagementTab({ deliveries, onNavigate, trainers = [], onRefresh }) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showAssign, setShowAssign] = useState(false);
  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelectedIds(new Set());

  // Build trainer → sessions map using trainer_1 and trainer_2 names
  const trainerMap = {};
  deliveries.forEach(d => {
    const addToMap = (name) => {
      if (!name) return;
      if (!trainerMap[name]) trainerMap[name] = [];
      trainerMap[name].push(d);
    };
    addToMap(d.trainer_1);
    addToMap(d.trainer_2);
  });

  // Merge with roster
  const rosterNames = new Set(trainers.map(t => t.name));
  const allKeys = new Set([...Object.keys(trainerMap), ...trainers.map(t => t.name)]);
  const rows = Array.from(allKeys).map(name => {
    const sessions = trainerMap[name] || [];
    const trainerInfo = trainers.find(t => t.name === name);
    const completed = sessions.filter(s => s.delivery_status === 'Completed').length;
    const contracted = sessions.filter(s => s.trainer_contract_status === 'Signed').length;
    return { name, sessions, completed, contracted, inRoster: rosterNames.has(name), trainerInfo };
  }).filter(r => r.sessions.length > 0 || r.inRoster);

  const filteredRows = search
    ? rows.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const toggleRow = (name) => setExpanded(e => ({ ...e, [name]: !e[name] }));

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground whitespace-nowrap">{filteredRows.length} trainers with PPRW assignments</p>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search trainer names…"
            className="pl-9" data-testid="trainer-mgmt-search" />
        </div>
      </div>
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 mb-4 p-3 rounded-xl border border-lime/40 bg-lime/5" data-testid="trainer-bulk-bar">
          <span className="text-sm font-medium">{selectedIds.size} session{selectedIds.size !== 1 ? 's' : ''} selected</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowAssign(true)} data-testid="assign-trainer-btn">
              <Users className="w-4 h-4" />Assign / Swap Trainer
            </Button>
            <Button size="sm" variant="ghost" onClick={clearSel} data-testid="trainer-clear-selection-btn">Clear</Button>
          </div>
        </div>
      )}
      <div className="space-y-3">
        {filteredRows.length === 0 && (
          <div className="text-center py-12 text-muted-foreground text-sm">
            {search ? 'No trainers match your search.' : 'No trainer assignments yet. Create deliveries and assign trainers to see them here.'}
          </div>
        )}
        {filteredRows.map(row => {
          const isOpen = !!expanded[row.name];
          return (
            <div key={row.name} className="rounded-xl border border-border overflow-hidden" data-testid={`trainer-row-${row.name}`}>
              <button
                className="w-full flex items-start justify-between p-4 text-left hover:bg-muted/20 transition-colors"
                onClick={() => toggleRow(row.name)}
                data-testid={`trainer-row-toggle-${row.name}`}
              >
                <div className="flex items-center gap-2">
                  <ChevronDown className={`w-4 h-4 text-muted-foreground flex-shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                  <div className="w-8 h-8 rounded-full bg-lime/20 flex items-center justify-center text-sm font-semibold text-lime-700 flex-shrink-0">
                    {row.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{row.name}</p>
                    {row.trainerInfo?.email && <p className="text-xs text-muted-foreground">{row.trainerInfo.email}</p>}
                  </div>
                </div>
                <div className="flex gap-4 text-center flex-shrink-0">
                  <div><p className="text-lg font-bold">{row.sessions.length}</p><p className="text-xs text-muted-foreground">Sessions</p></div>
                  <div><p className="text-lg font-bold text-emerald-600">{row.completed}</p><p className="text-xs text-muted-foreground">Completed</p></div>
                  <div><p className="text-lg font-bold text-blue-600">{row.contracted}</p><p className="text-xs text-muted-foreground">Contracted</p></div>
                </div>
              </button>
              {isOpen && row.sessions.length > 0 && (
                <div className="overflow-x-auto border-t border-border px-4 pb-4" data-testid={`trainer-row-body-${row.name}`}>
                  <table className="w-full text-xs mt-3">
                    <thead><tr className="border-b border-border">
                      <th className="py-1.5 w-8"></th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Course</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Date</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">TZ</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Contract</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Content</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Lab Access</th>
                      <th className="py-1.5 text-left font-medium text-muted-foreground">Status</th>
                    </tr></thead>
                    <tbody>
                      {row.sessions.map(s => (
                        <tr key={s.id} className="border-b border-border/50 hover:bg-muted/20 cursor-pointer" onClick={() => onNavigate(s.id)}>
                          <td className="py-1.5" onClick={e => e.stopPropagation()}>
                            <Checkbox checked={selectedIds.has(s.id)} onCheckedChange={() => toggleSelect(s.id)} data-testid={`trainer-session-select-${s.id}`} />
                          </td>
                          <td className="py-1.5 max-w-[180px] truncate font-medium">{s.course_title}</td>
                          <td className="py-1.5 whitespace-nowrap">{fmtDate(s.start_date)}</td>
                          <td className="py-1.5">{tzBadge(s.time_zone)}</td>
                          <td className="py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.trainer_contract_status === 'Signed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              {s.trainer_contract_status}
                            </span>
                          </td>
                          <td className="py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.trainer_content_status === 'Shared' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                              {s.trainer_content_status}
                            </span>
                          </td>
                          <td className="py-1.5">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${s.lab_access_status === 'Shared' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                              {s.lab_access_status}
                            </span>
                          </td>
                          <td className="py-1.5">{statusBadge(s.delivery_status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {showAssign && (
        <AssignTrainerDialog open={showAssign} onClose={() => setShowAssign(false)} sessionIds={Array.from(selectedIds)} trainers={trainers} allDeliveries={deliveries}
          onDone={() => { if (onRefresh) onRefresh(); clearSel(); }} />
      )}
    </div>
  );
}

// ── Dashboard Sub-components (module level to avoid re-render issues) ─────────
const KpiCard = ({ label, value, sub, color = 'text-foreground', onClick, breakdown }) => (
  <div
    className={`rounded-xl border border-border bg-background p-5 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-lime/50 transition-all' : ''}`}
    onClick={onClick}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{label}</p>
    <p className={`text-3xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    {breakdown && breakdown.length > 0 && (
      <div className="mt-2.5 pt-2.5 border-t border-border/60 space-y-1">
        {breakdown.slice(0, 4).map(b => (
          <div key={b.name} className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span className="truncate max-w-[70%]">{b.name}</span>
            <span className="font-semibold tabular-nums text-foreground">{b.value}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

// ── Drill-Down Modal (consistent everywhere on the PPRW Dashboard) ────────────
function DrillDownModal({ open, onClose, title, records, filterSummary }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto" data-testid="pprw-drilldown-modal">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{filterSummary} · {records.length} matching record{records.length !== 1 ? 's' : ''}</DialogDescription>
        </DialogHeader>
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/50">
              <tr>
                {['Course', 'Quarter', 'Solution Area', 'Delivery Date', 'TZ', 'Type', 'Status', 'Attendance'].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map(d => (
                <tr key={d.id} className="border-t border-border">
                  <td className="px-3 py-2 max-w-[220px] truncate font-medium">{d.course_title}</td>
                  <td className="px-3 py-2">{d.quarter || '—'}</td>
                  <td className="px-3 py-2 max-w-[140px] truncate">{d.solution_area || '—'}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDate(d.start_date)}</td>
                  <td className="px-3 py-2">{tzBadge(d.time_zone)}</td>
                  <td className="px-3 py-2">{d.delivery_type || '—'}</td>
                  <td className="px-3 py-2">{statusBadge(d.delivery_status)}</td>
                  <td className="px-3 py-2 font-medium">{d.attendance != null ? d.attendance : '—'}</td>
                </tr>
              ))}
              {records.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No matching records</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end mt-2">
          <Button variant="outline" onClick={onClose} data-testid="pprw-drilldown-close-btn">Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ClickableDot = (props) => {
  const { cx, cy, payload, onDotClick } = props;
  if (cx == null || cy == null) return null;
  return (
    <circle cx={cx} cy={cy} r={4} fill="#84cc16" stroke="#fff" strokeWidth={1.5}
      style={{ cursor: 'pointer' }} onClick={() => onDotClick && onDotClick(payload)} />
  );
};

const STATUS_SEGMENT_COLORS = { Scheduled: '#3b82f6', Delivered: '#10b981', Cancelled: '#ef4444' };

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function DashboardTab({ deliveries }) {
  const [filters, setFilters] = useState({ startDate: '', endDate: '', quarter: '', solutionArea: '', timeZone: '', deliveryType: '', deliveryStatus: '' });
  const [drillDown, setDrillDown] = useState(null);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ startDate: '', endDate: '', quarter: '', solutionArea: '', timeZone: '', deliveryType: '', deliveryStatus: '' });
  const hasFilters = Object.values(filters).some(Boolean);

  const filtered = React.useMemo(() => deliveries.filter(d => {
    if (filters.startDate && (!d.start_date || d.start_date < filters.startDate)) return false;
    if (filters.endDate && (!d.start_date || d.start_date > filters.endDate)) return false;
    if (filters.quarter && d.quarter !== filters.quarter) return false;
    if (filters.solutionArea && d.solution_area !== filters.solutionArea) return false;
    if (filters.timeZone && d.time_zone !== filters.timeZone) return false;
    if (filters.deliveryType && d.delivery_type !== filters.deliveryType) return false;
    if (filters.deliveryStatus && d.delivery_status !== filters.deliveryStatus) return false;
    return true;
  }), [deliveries, filters]);

  const filterSummaryText = () => {
    const parts = [];
    if (filters.startDate) parts.push(`from ${filters.startDate}`);
    if (filters.endDate) parts.push(`to ${filters.endDate}`);
    if (filters.quarter) parts.push(filters.quarter);
    if (filters.solutionArea) parts.push(filters.solutionArea);
    if (filters.timeZone) parts.push(filters.timeZone);
    if (filters.deliveryType) parts.push(filters.deliveryType);
    if (filters.deliveryStatus) parts.push(filters.deliveryStatus);
    return parts.length ? `Filters: ${parts.join(', ')}` : 'No dashboard filters applied';
  };

  const bySolutionArea = (list) => {
    const map = {};
    list.forEach(d => { const sa = d.solution_area || 'Unknown'; map[sa] = (map[sa] || 0) + 1; });
    return Object.entries(map).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };

  const completed = filtered.filter(d => d.delivery_status === 'Completed');
  const scheduled = filtered.filter(d => d.delivery_status === 'Scheduled');
  const cancelled = filtered.filter(d => d.delivery_status === 'Cancelled');
  const totalLearners = completed.reduce((s, d) => s + (d.attendance || 0), 0);

  const uniqueMap = new Map();
  filtered.forEach(d => {
    const key = `${d.course_title}__${d.start_date}`;
    if (!uniqueMap.has(key)) uniqueMap.set(key, d);
  });
  const uniqueRecords = Array.from(uniqueMap.values());

  const openDrillDown = (title, records) => setDrillDown({ title, records, filterSummary: filterSummaryText() });

  // Visual 1: Sessions by Solution Area
  const sessionsBySA = bySolutionArea(filtered);
  // Visual 2: Status by Solution Area (stacked: Scheduled / Delivered(Completed) / Cancelled)
  // Includes any legacy/unknown solution-area values so totals reconcile with Total Workshop Sessions
  const allSolutionAreas = Array.from(new Set([...SOLUTION_AREAS, ...filtered.map(d => d.solution_area || 'Other')]));
  const statusBySA = allSolutionAreas.map(sa => {
    const inSA = filtered.filter(d => (d.solution_area || 'Other') === sa);
    return {
      name: sa,
      Scheduled: inSA.filter(d => d.delivery_status === 'Scheduled').length,
      Delivered: inSA.filter(d => d.delivery_status === 'Completed').length,
      Cancelled: inSA.filter(d => d.delivery_status === 'Cancelled').length,
    };
  }).filter(row => row.Scheduled + row.Delivered + row.Cancelled > 0 || SOLUTION_AREAS.includes(row.name));
  // Visual 3: Delivery Type
  const typeMap = {};
  filtered.forEach(d => { const t = d.delivery_type || 'Unknown'; typeMap[t] = (typeMap[t] || 0) + 1; });
  const typeChart = Object.entries(typeMap).map(([name, value]) => ({ name, value }));
  // Visual 4: Monthly trend
  const monthMap = {};
  filtered.forEach(d => { if (d.start_date) { const mk = d.start_date.slice(0, 7); monthMap[mk] = (monthMap[mk] || 0) + 1; } });
  const monthlyTrend = Object.entries(monthMap).sort(([a], [b]) => a.localeCompare(b)).map(([month, sessions]) => ({ month, sessions }));
  // Visuals 5-8: Top 3 Courses by Attendance, one per Solution Area
  const topCoursesBySA = SOLUTION_AREAS.map(sa => {
    const inSA = filtered.filter(d => (d.solution_area || 'Unknown') === sa);
    const courseMap = {};
    inSA.forEach(d => { const c = d.course_title || 'Unknown'; courseMap[c] = (courseMap[c] || 0) + (d.attendance || 0); });
    const top3 = Object.entries(courseMap).map(([name, attendance]) => ({ name, attendance }))
      .filter(c => c.attendance > 0)
      .sort((a, b) => b.attendance - a.attendance).slice(0, 3);
    return { solutionArea: sa, courses: top3 };
  });

  return (
    <div className="space-y-6" data-testid="pprw-dashboard-tab">
      {/* Global Filters */}
      <div className="rounded-xl border border-border bg-background p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground mb-1">From</label>
            <Input type="date" value={filters.startDate} onChange={e => setFilter('startDate', e.target.value)} className="w-36 h-9" data-testid="dash-filter-start-date" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground mb-1">To</label>
            <Input type="date" value={filters.endDate} onChange={e => setFilter('endDate', e.target.value)} className="w-36 h-9" data-testid="dash-filter-end-date" />
          </div>
          <Select value={filters.quarter} onValueChange={v => setFilter('quarter', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-28 h-9" data-testid="dash-filter-quarter"><SelectValue placeholder="Quarter" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Quarters</SelectItem>{QUARTERS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.solutionArea} onValueChange={v => setFilter('solutionArea', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48 h-9" data-testid="dash-filter-solution-area"><SelectValue placeholder="Solution Area" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Solution Areas</SelectItem>{SOLUTION_AREAS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.timeZone} onValueChange={v => setFilter('timeZone', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-28 h-9" data-testid="dash-filter-tz"><SelectValue placeholder="Time Zone" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All TZs</SelectItem>{TIMEZONES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.deliveryType} onValueChange={v => setFilter('deliveryType', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32 h-9" data-testid="dash-filter-delivery-type"><SelectValue placeholder="Delivery Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem>{DELIVERY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.deliveryStatus} onValueChange={v => setFilter('deliveryStatus', v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36 h-9" data-testid="dash-filter-status"><SelectValue placeholder="Delivery Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{DELIVERY_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-muted-foreground h-9" data-testid="dash-clear-filters"><X className="w-3.5 h-3.5" />Clear</Button>}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <KpiCard label="Total Learners Trained" value={totalLearners.toLocaleString()} color="text-emerald-600"
          sub="Sum of attendance, completed sessions only"
          onClick={() => openDrillDown('Total Learners Trained — Completed Sessions', completed)} />
        <KpiCard label="Total Workshop Sessions" value={filtered.length} color="text-foreground"
          breakdown={sessionsBySA}
          onClick={() => openDrillDown('Total Workshop Sessions', filtered)} />
        <KpiCard label="Scheduled Workshop Sessions" value={scheduled.length} color="text-blue-600"
          breakdown={bySolutionArea(scheduled)}
          onClick={() => openDrillDown('Scheduled Workshop Sessions', scheduled)} />
        <KpiCard label="Delivered Workshop Sessions" value={completed.length} color="text-emerald-600"
          breakdown={bySolutionArea(completed)}
          onClick={() => openDrillDown('Delivered Workshop Sessions', completed)} />
        <KpiCard label="Cancelled Workshop Sessions" value={cancelled.length} color="text-red-600"
          breakdown={bySolutionArea(cancelled)}
          onClick={() => openDrillDown('Cancelled Workshop Sessions', cancelled)} />
        <KpiCard label="Unique Workshop Deliveries" value={uniqueRecords.length} color="text-violet-600"
          sub="Course + delivery date, time zone excluded"
          onClick={() => openDrillDown('Unique Workshop Deliveries', uniqueRecords)} />
      </div>

      {/* Visuals row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="text-sm font-semibold mb-4">Workshop Sessions by Solution Area</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sessionsBySA} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} allowDecimals={false} />
              <RechartsTooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} cursor="pointer"
                onClick={(d) => openDrillDown(`Workshop Sessions — ${d.name}`, filtered.filter(x => (x.solution_area || 'Unknown') === d.name))}>
                {sessionsBySA.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="text-sm font-semibold mb-4">Workshop Status by Solution Area</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusBySA} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} allowDecimals={false} />
              <RechartsTooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {['Scheduled', 'Delivered', 'Cancelled'].map((seg, idx) => (
                <Bar key={seg} dataKey={seg} stackId="status" fill={STATUS_SEGMENT_COLORS[seg]} cursor="pointer"
                  radius={idx === 2 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                  onClick={(d) => {
                    const statusVal = seg === 'Delivered' ? 'Completed' : seg;
                    openDrillDown(`${seg} — ${d.name}`, filtered.filter(x => (x.solution_area || 'Other') === d.name && x.delivery_status === statusVal));
                  }} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Visuals row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="text-sm font-semibold mb-4">Session Delivery Type</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={typeChart} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75}
                onClick={(d) => openDrillDown(`Delivery Type — ${d.name}`, filtered.filter(x => (x.delivery_type || 'Unknown') === d.name))}
                cursor="pointer">
                {typeChart.map((entry, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <RechartsTooltip />
              <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 11 }}
                formatter={(_, entry) => `${entry.payload.name} (${entry.payload.value})`} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-background p-5">
          <h3 className="text-sm font-semibold mb-4">Monthly Workshop Session Trend</h3>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={monthlyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} tickLine={false} />
              <YAxis tick={{ fontSize: 10 }} tickLine={false} allowDecimals={false} />
              <RechartsTooltip />
              <Line type="monotone" dataKey="sessions" stroke="#84cc16" strokeWidth={2}
                dot={(props) => <ClickableDot {...props} key={`dot-${props.payload?.month}`} onDotClick={(payload) => openDrillDown(`Workshop Sessions — ${payload.month}`, filtered.filter(x => x.start_date && x.start_date.slice(0, 7) === payload.month))} />} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Visuals 5-8: Top 3 Courses by Attendance, one per Solution Area */}
      <div>
        <h3 className="text-sm font-semibold mb-3">Top 3 Courses by Attendance</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {topCoursesBySA.map(({ solutionArea, courses }) => (
            <div key={solutionArea} className="rounded-xl border border-border bg-background p-5">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">{solutionArea}</h4>
              {courses.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(120, courses.length * 50)}>
                  <BarChart data={courses} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10 }} tickLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} tickLine={false} width={160}
                      tickFormatter={(v) => v.length > 22 ? `${v.slice(0, 22)}…` : v} />
                    <RechartsTooltip />
                    <Bar dataKey="attendance" fill="#3b82f6" radius={[0, 4, 4, 0]} cursor="pointer"
                      onClick={(d) => openDrillDown(`${d.name} — ${solutionArea}`, filtered.filter(x => (x.solution_area || 'Unknown') === solutionArea && x.course_title === d.name))} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8 text-xs">No attendance data yet</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {drillDown && (
        <DrillDownModal open={!!drillDown} onClose={() => setDrillDown(null)}
          title={drillDown.title} records={drillDown.records} filterSummary={drillDown.filterSummary} />
      )}
    </div>
  );
}

// ── PO Tracker Tab (reuses the global Purchase Orders module, scoped to PPRW) ─
const LAB_USAGE_STATUSES = ['Recorded', 'Billed', 'Pending Review'];
const PO_STATUSES = ['active', 'exhausted', 'expired', 'cancelled'];

function PODialog({ open, onClose, target, onSaved }) {
  const [form, setForm] = useState({
    po_number: '', owner: '', total_amount: '', currency: 'USD',
    issue_date: '', expiry_date: '', description: '', sow_reference: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        po_number: target.po_number || '', owner: target.owner || '',
        total_amount: String(target.total_amount ?? ''), currency: target.currency || 'USD',
        issue_date: target.issue_date || '', expiry_date: target.expiry_date || '',
        description: target.description || '', sow_reference: target.sow_reference || '',
      });
    } else {
      setForm({ po_number: '', owner: '', total_amount: '', currency: 'USD', issue_date: new Date().toISOString().slice(0, 10), expiry_date: '', description: '', sow_reference: '' });
    }
  }, [target, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.po_number.trim() || !form.total_amount || !form.issue_date) { toast.error('PO Number, Budget and Issue Date are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, total_amount: parseFloat(form.total_amount), expiry_date: form.expiry_date || null };
      if (target) await purchaseOrdersAPI.update(target.id, payload);
      else await purchaseOrdersAPI.create(payload);
      toast.success(target ? 'PO updated' : 'PO created');
      onSaved();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="po-tracker-dialog">
        <DialogHeader><DialogTitle>{target ? 'Edit Purchase Order' : 'New Purchase Order'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">PO Number *</label>
            <Input value={form.po_number} onChange={e => set('po_number', e.target.value)} data-testid="po-form-number" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">Owner</label>
            <Input value={form.owner} onChange={e => set('owner', e.target.value)} placeholder="e.g. Pooja Maurya" data-testid="po-form-owner" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Budget ($) *</label>
            <Input type="number" value={form.total_amount} onChange={e => set('total_amount', e.target.value)} data-testid="po-form-amount" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Currency</label>
            <Input value={form.currency} onChange={e => set('currency', e.target.value)} data-testid="po-form-currency" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Issue Date *</label>
            <Input type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} data-testid="po-form-issue-date" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Expiry Date (Validity)</label>
            <Input type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} data-testid="po-form-expiry-date" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">SOW Reference</label>
            <Input value={form.sow_reference} onChange={e => set('sow_reference', e.target.value)} data-testid="po-form-sow" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">Description</label>
            <Input value={form.description} onChange={e => set('description', e.target.value)} data-testid="po-form-description" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="po-form-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="po-form-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function POExtensionDialog({ open, onClose, po, onSaved }) {
  const [form, setForm] = useState({ po_number: '', amount: '', issue_date: new Date().toISOString().slice(0, 10), description: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.po_number.trim() || !form.amount) { toast.error('Extension PO Number and Amount are required'); return; }
    setSaving(true);
    try {
      await purchaseOrdersAPI.addExtension(po.id, { ...form, amount: parseFloat(form.amount) });
      toast.success('Extension added');
      onSaved();
      onClose();
      setForm({ po_number: '', amount: '', issue_date: new Date().toISOString().slice(0, 10), description: '' });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add extension'); }
    finally { setSaving(false); }
  };

  if (!po) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="po-extension-dialog">
        <DialogHeader>
          <DialogTitle>Add Extension — Parent PO {po.po_number}</DialogTitle>
          <DialogDescription>Current Total Amount: ${(po.consolidated_total_amount ?? po.total_amount ?? 0).toLocaleString()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Extension PO Number *</label>
            <Input value={form.po_number} onChange={e => set('po_number', e.target.value)} data-testid="po-ext-number" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Extension Amount ($) *</label>
            <Input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="po-ext-amount" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Issue Date</label>
            <Input type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} data-testid="po-ext-issue-date" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Description</label>
            <Input value={form.description} onChange={e => set('description', e.target.value)} data-testid="po-ext-description" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose} data-testid="po-ext-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="po-ext-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Extension'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LabUsageDialog({ open, onClose, target, deliveries, onSaved }) {
  const [form, setForm] = useState({ po_number: '', delivery_id: '', workshop_title: '', session_date: '', num_learners: '', cost_per_learner: '', status: 'Recorded' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setForm({
        po_number: target.po_number || '', delivery_id: target.delivery_id || '',
        workshop_title: target.workshop_title || '', session_date: target.session_date || '',
        num_learners: String(target.num_learners ?? ''), cost_per_learner: String(target.cost_per_learner ?? ''),
        status: target.status || 'Recorded',
      });
    } else {
      setForm({ po_number: '', delivery_id: '', workshop_title: '', session_date: '', num_learners: '', cost_per_learner: '', status: 'Recorded' });
    }
  }, [target, open]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickDelivery = (id) => {
    const d = deliveries.find(x => x.id === id);
    setForm(f => ({ ...f, delivery_id: id, workshop_title: d ? d.course_title : f.workshop_title, session_date: d ? d.start_date : f.session_date, po_number: d?.po_number || f.po_number }));
  };

  const handleSave = async () => {
    if (!form.po_number.trim() || !form.workshop_title.trim()) { toast.error('PO Number and Workshop are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, num_learners: parseInt(form.num_learners || 0, 10), cost_per_learner: parseFloat(form.cost_per_learner || 0) };
      if (target) await pprwAPI.updateLabUsage(target.id, payload);
      else await pprwAPI.createLabUsage(payload);
      toast.success(target ? 'Usage entry updated' : 'Usage entry recorded');
      onSaved();
      onClose();
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="lab-usage-dialog">
        <DialogHeader><DialogTitle>{target ? 'Edit Lab Usage Entry' : 'Record Lab Usage'}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">Linked Workshop (optional)</label>
            <Select value={form.delivery_id} onValueChange={pickDelivery}>
              <SelectTrigger data-testid="lab-usage-delivery"><SelectValue placeholder="Select a PPRW workshop…" /></SelectTrigger>
              <SelectContent>{deliveries.map(d => <SelectItem key={d.id} value={d.id}>{d.course_title} — {fmtDate(d.start_date)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">Workshop Title *</label>
            <Input value={form.workshop_title} onChange={e => set('workshop_title', e.target.value)} data-testid="lab-usage-workshop" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">PO Number *</label>
            <Input value={form.po_number} onChange={e => set('po_number', e.target.value)} data-testid="lab-usage-po" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Session Date</label>
            <Input type="date" value={form.session_date || ''} onChange={e => set('session_date', e.target.value)} data-testid="lab-usage-session-date" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Number of Learners</label>
            <Input type="number" value={form.num_learners} onChange={e => set('num_learners', e.target.value)} data-testid="lab-usage-learners" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Cost per Learner ($)</label>
            <Input type="number" value={form.cost_per_learner} onChange={e => set('cost_per_learner', e.target.value)} data-testid="lab-usage-cost" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium mb-1">Status</label>
            <Select value={form.status} onValueChange={v => set('status', v)}>
              <SelectTrigger data-testid="lab-usage-status"><SelectValue /></SelectTrigger>
              <SelectContent>{LAB_USAGE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-2 text-right text-sm text-muted-foreground">
            Total Lab Cost: <span className="font-semibold text-foreground">${((parseInt(form.num_learners || 0, 10)) * (parseFloat(form.cost_per_learner || 0))).toLocaleString()}</span>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={onClose} data-testid="lab-usage-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="lab-usage-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function POTrackerTab({ deliveries }) {
  const [pos, setPos] = useState([]);
  const [labUsage, setLabUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showPOForm, setShowPOForm] = useState(false);
  const [editPO, setEditPO] = useState(null);
  const [extensionPO, setExtensionPO] = useState(null);
  const [expandedPO, setExpandedPO] = useState({});
  const [showLabForm, setShowLabForm] = useState(false);
  const [editLabEntry, setEditLabEntry] = useState(null);
  const [labSearch, setLabSearch] = useState('');
  const [labStartDate, setLabStartDate] = useState('');
  const [labEndDate, setLabEndDate] = useState('');

  const loadPOs = useCallback(async () => {
    try { setPos(await purchaseOrdersAPI.getAll({ limit: 500 })); }
    catch { toast.error('Failed to load Purchase Orders'); }
    finally { setLoading(false); }
  }, []);

  const loadLabUsage = useCallback(async () => {
    try { setLabUsage(await pprwAPI.getLabUsage()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadPOs(); loadLabUsage(); }, [loadPOs, loadLabUsage]);

  const linkedWorkshops = (poNumber) => deliveries.filter(d => (d.po_number || '').toUpperCase() === (poNumber || '').toUpperCase());

  const filteredPOs = pos.filter(po => {
    if (statusFilter && po.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return po.po_number.toLowerCase().includes(s) || (po.owner || '').toLowerCase().includes(s) || (po.description || '').toLowerCase().includes(s);
    }
    return true;
  });

  const filteredLabUsage = labUsage.filter(entry => {
    if (labStartDate && (!entry.session_date || entry.session_date < labStartDate)) return false;
    if (labEndDate && (!entry.session_date || entry.session_date > labEndDate)) return false;
    if (labSearch) {
      const s = labSearch.toLowerCase();
      return (entry.po_number || '').toLowerCase().includes(s) || (entry.workshop_title || '').toLowerCase().includes(s);
    }
    return true;
  });
  const hasLabFilters = labSearch || labStartDate || labEndDate;
  const clearLabFilters = () => { setLabSearch(''); setLabStartDate(''); setLabEndDate(''); };

  const handleDeleteLab = async (id) => {
    if (!window.confirm('Delete this lab usage entry?')) return;
    try { await pprwAPI.deleteLabUsage(id); toast.success('Deleted'); loadLabUsage(); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-8" data-testid="po-tracker-tab">
      {/* Purchase Orders */}
      <div>
        <div className="flex flex-wrap gap-3 items-center mb-4">
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search PO number, owner, description…" className="pl-9" data-testid="po-tracker-search" />
          </div>
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36" data-testid="po-tracker-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{PO_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" className="gap-2 ml-auto" onClick={() => { setEditPO(null); setShowPOForm(true); }} data-testid="po-tracker-new-btn">
            <Plus className="w-4 h-4" />New PO
          </Button>
        </div>

        <div className="rounded-xl border border-border overflow-hidden" data-testid="po-tracker-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['PO Number', 'Owner', 'Budget', 'Amount Utilized', 'Remaining Balance', 'Validity', 'Linked Workshops', 'Status', ''].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPOs.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground text-sm">No Purchase Orders found.</td></tr>
              )}
              {filteredPOs.map(po => {
                const workshops = linkedWorkshops(po.po_number);
                const isOpen = !!expandedPO[po.id];
                return (
                  <React.Fragment key={po.id}>
                    <tr className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`po-row-${po.po_number}`}>
                      <td className="px-3 py-3 font-medium">{po.po_number}</td>
                      <td className="px-3 py-3 text-xs">{po.owner || '—'}</td>
                      <td className="px-3 py-3 text-xs">${(po.consolidated_total_amount ?? po.total_amount ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-xs">${(po.used_amount ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-xs font-medium text-emerald-600">${(po.available_balance ?? 0).toLocaleString()}</td>
                      <td className="px-3 py-3 text-xs whitespace-nowrap">{po.issue_date} → {po.expiry_date || '—'}</td>
                      <td className="px-3 py-3">
                        <button className="text-xs text-primary underline" onClick={() => setExpandedPO(e => ({ ...e, [po.id]: !e[po.id] }))} data-testid={`po-linked-toggle-${po.po_number}`}>
                          {workshops.length} workshop{workshops.length !== 1 ? 's' : ''}
                        </button>
                      </td>
                      <td className="px-3 py-3"><Badge variant="outline" className="text-xs capitalize">{po.status}</Badge></td>
                      <td className="px-3 py-3">
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setExtensionPO(po)} data-testid={`po-add-extension-${po.po_number}`}>+ Extension</Button>
                        <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => { setEditPO(po); setShowPOForm(true); }} data-testid={`po-edit-${po.po_number}`}>Edit</Button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-t border-border/50 bg-muted/10">
                        <td colSpan={9} className="px-6 py-3">
                          {workshops.length === 0 ? (
                            <p className="text-xs text-muted-foreground">No PPRW workshops reference this PO number yet.</p>
                          ) : (
                            <ul className="text-xs space-y-1">
                              {workshops.map(w => (
                                <li key={w.id} className="flex items-center justify-between">
                                  <Link to={`/pprw/${w.id}`} className="hover:underline">{w.course_title} — {fmtDate(w.start_date)}</Link>
                                  {statusBadge(w.delivery_status)}
                                </li>
                              ))}
                            </ul>
                          )}
                          {po.extensions_count > 0 && (
                            <p className="text-xs text-muted-foreground mt-2">{po.extensions_count} extension(s) totaling ${po.extensions_total.toLocaleString()}</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Interactive Simulated Lab Usage */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">Interactive Simulated Lab Usage &amp; Consumption</h3>
          <Button size="sm" className="gap-2" onClick={() => { setEditLabEntry(null); setShowLabForm(true); }} data-testid="lab-usage-new-btn">
            <Plus className="w-4 h-4" />Record Usage
          </Button>
        </div>
        <div className="flex flex-wrap gap-3 items-end mb-4">
          <div className="relative flex-1 min-w-52">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={labSearch} onChange={e => setLabSearch(e.target.value)} placeholder="Search PO number or workshop…" className="pl-9" data-testid="lab-usage-search" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground mb-1">From</label>
            <Input type="date" value={labStartDate} onChange={e => setLabStartDate(e.target.value)} className="w-36 h-9" data-testid="lab-usage-filter-start-date" />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-muted-foreground mb-1">To</label>
            <Input type="date" value={labEndDate} onChange={e => setLabEndDate(e.target.value)} className="w-36 h-9" data-testid="lab-usage-filter-end-date" />
          </div>
          {hasLabFilters && <Button variant="ghost" size="sm" onClick={clearLabFilters} className="gap-1 text-muted-foreground h-9" data-testid="lab-usage-clear-filters"><X className="w-3.5 h-3.5" />Clear</Button>}
        </div>
        <div className="rounded-xl border border-border overflow-hidden" data-testid="lab-usage-table">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                {['PO Number', 'Workshop', 'Session Date', 'Learners', 'Cost / Learner', 'Total Lab Cost', 'Status', ''].map(h => (
                  <th key={h} className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLabUsage.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground text-sm">
                  {labUsage.length === 0 ? 'No lab usage recorded yet.' : 'No lab usage entries match your filters.'}
                </td></tr>
              )}
              {filteredLabUsage.map(entry => (
                <tr key={entry.id} className="border-t border-border hover:bg-muted/30 transition-colors" data-testid={`lab-usage-row-${entry.id}`}>
                  <td className="px-3 py-3 text-xs font-medium">{entry.po_number}</td>
                  <td className="px-3 py-3 text-xs max-w-[200px] truncate">{entry.workshop_title}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{fmtDate(entry.session_date)}</td>
                  <td className="px-3 py-3 text-xs">{entry.num_learners}</td>
                  <td className="px-3 py-3 text-xs">${entry.cost_per_learner}</td>
                  <td className="px-3 py-3 text-xs font-medium">${entry.total_lab_cost.toLocaleString()}</td>
                  <td className="px-3 py-3"><Badge variant="outline" className="text-xs">{entry.status}</Badge></td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      <button className="p-1.5 rounded hover:bg-muted" title="Edit" onClick={() => { setEditLabEntry(entry); setShowLabForm(true); }} data-testid={`lab-usage-edit-${entry.id}`}>
                        <Eye className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      <button className="p-1.5 rounded hover:bg-red-50" title="Delete" onClick={() => handleDeleteLab(entry.id)} data-testid={`lab-usage-delete-${entry.id}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PODialog open={showPOForm} onClose={() => setShowPOForm(false)} target={editPO} onSaved={loadPOs} />
      <POExtensionDialog open={!!extensionPO} onClose={() => setExtensionPO(null)} po={extensionPO} onSaved={loadPOs} />
      <LabUsageDialog open={showLabForm} onClose={() => setShowLabForm(false)} target={editLabEntry} deliveries={deliveries} onSaved={loadLabUsage} />
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function PPRWPage() {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracker');
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);

  const loadDeliveries = useCallback(async () => {
    setLoading(true);
    try { setDeliveries(await pprwAPI.getAll()); }
    catch (_e) { toast.error('Failed to load deliveries'); }
    finally { setLoading(false); }
  }, []);

  const loadTrainers = useCallback(async () => {
    try { setTrainers(await trainersAPI.getAll()); }
    catch (_e) { /* trainer roster unavailable */ }
  }, []);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);
  useEffect(() => { loadTrainers(); }, [loadTrainers]);
  useEffect(() => { taxonomyAPI.getAll().then(setTaxonomyCategories).catch(() => {}); }, []);

  const [topTab, setTopTab] = useState('sessions');
  const tabs = [
    { id: 'tracker', label: 'Training Tracker', icon: LayoutGrid },
    { id: 'calendar', label: 'Calendar View', icon: Calendar },
    { id: 'trainers', label: 'Trainer Management', icon: Users },
    { id: 'po-tracker', label: 'PO Tracker', icon: Wallet },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart2 },
  ];

  const topTabs = [
    { id: 'workshops', label: 'Workshops', icon: Layers },
    { id: 'sessions', label: 'Sessions', icon: MonitorPlay },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="pprw-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <MonitorPlay className="w-5 h-5 text-lime" />
            <h1 className="text-2xl font-bold">1:Many PPRW</h1>
          </div>
          <p className="text-sm text-muted-foreground">Partner Pre-Release Workshops — group sessions into workshops and manage virtual ILT sessions at scale</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="text-right">
            <p className="text-2xl font-bold">{deliveries.length}</p>
            <p className="text-xs text-muted-foreground">Total Sessions</p>
          </div>
        </div>
      </div>

      {/* Top-level tabs: Workshops | Sessions */}
      <div className="inline-flex rounded-xl border border-border p-1 bg-muted/40" data-testid="pprw-top-tabs">
        {topTabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTopTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-lg transition-all ${
                topTab === t.id ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`pprw-top-tab-${t.id}`}>
              <Icon className="w-4 h-4" />{t.label}
            </button>
          );
        })}
      </div>

      {topTab === 'workshops' ? (
        <WorkshopsTab categories={taxonomyCategories} />
      ) : (
        <>
          {/* Session sub-tabs */}
          <div className="border-b border-border">
            <div className="flex gap-0">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                      activeTab === tab.id
                        ? 'border-lime text-foreground'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                    data-testid={`pprw-tab-${tab.id}`}>
                    <Icon className="w-4 h-4" />{tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          {loading && activeTab !== 'dashboard' ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div>
              {activeTab === 'tracker' && <TrainingTrackerTab deliveries={deliveries} onRefresh={loadDeliveries} onNavigate={id => navigate(`/pprw/${id}`)} trainers={trainers} categories={taxonomyCategories} />}
              {activeTab === 'calendar' && <CalendarTab deliveries={deliveries} onNavigate={id => navigate(`/pprw/${id}`)} />}
              {activeTab === 'trainers' && <TrainerManagementTab deliveries={deliveries} onNavigate={id => navigate(`/pprw/${id}`)} trainers={trainers} onRefresh={loadDeliveries} />}
              {activeTab === 'po-tracker' && <POTrackerTab deliveries={deliveries} />}
              {activeTab === 'dashboard' && <DashboardTab deliveries={deliveries} />}
            </div>
          )}
        </>
      )}
    </div>
  );
}
